/**
 * Media Gallery Slider Block - Frontend View Script
 * Initialize Swiper.js for the slider functionality
 * https://swiperjs.com/get-started
 */

// Import Swiper bundle with all modules installed
import Swiper from 'swiper/bundle';

/**
 * Check if a slide contains a video player block
 * @param {HTMLElement} slide - Swiper slide element
 * @returns {HTMLElement|null} Video player element or null
 */
function getVideoPlayerFromSlide(slide) {
  return slide?.querySelector('.video-player');
}

/**
 * Play video in a slide if it contains a video player
 * @param {HTMLElement} slide - Swiper slide element
 */
function playSlideVideo(slide) {
  const videoPlayer = getVideoPlayerFromSlide(slide);
  if (videoPlayer && window.videoPlayerAPI) {
    window.videoPlayerAPI.play(videoPlayer);
  }
}

/**
 * Pause video in a slide if it contains a video player
 * @param {HTMLElement} slide - Swiper slide element
 */
function pauseSlideVideo(slide) {
  const videoPlayer = getVideoPlayerFromSlide(slide);
  if (videoPlayer && window.videoPlayerAPI) {
    window.videoPlayerAPI.pause(videoPlayer);
  }
}

/**
 * Pause all videos in the slider except the active one
 * @param {Swiper} swiper - Swiper instance
 * @param {number} activeIndex - Index of the active slide
 */
function pauseAllVideosExcept(swiper, activeIndex) {
  const slides = swiper.slides;
  slides.forEach((slide, index) => {
    if (index !== activeIndex) {
      pauseSlideVideo(slide);
    }
  });
}

/**
 * Initialize a single slider instance
 */
function initSlider(sliderElement) {
  // Get configuration from data attributes
  const navigationStyle = sliderElement.dataset.navigationStyle || 'arrows';
  const direction = sliderElement.dataset.direction || 'horizontal';
  
  // Parse slidesPerView - allow float values like 1.5
  const slidesPerViewMobileParsed = parseFloat(sliderElement.dataset.slidesPerViewMobile);
  const slidesPerViewMobile = isNaN(slidesPerViewMobileParsed) ? 1 : slidesPerViewMobileParsed;
  
  const slidesPerViewDesktopParsed = parseFloat(sliderElement.dataset.slidesPerViewDesktop);
  const slidesPerViewDesktop = isNaN(slidesPerViewDesktopParsed) ? 3 : slidesPerViewDesktopParsed;
  
  // Handle spaceBetween - need to allow 0 value
  const spaceBetweenMobileParsed = parseInt(sliderElement.dataset.spaceBetweenMobile);
  const spaceBetweenMobile = isNaN(spaceBetweenMobileParsed) ? 10 : spaceBetweenMobileParsed;
  
  const spaceBetweenDesktopParsed = parseInt(sliderElement.dataset.spaceBetweenDesktop);
  const spaceBetweenDesktop = isNaN(spaceBetweenDesktopParsed) ? 30 : spaceBetweenDesktopParsed;
  
  const centeredSlidesMobile = sliderElement.dataset.centeredSlidesMobile === 'true';
  const centeredSlidesDesktop = sliderElement.dataset.centeredSlidesDesktop === 'true';
  const initialSlide = parseInt(sliderElement.dataset.initialSlide) || 0;

  const swiperElement = sliderElement.querySelector('.media-gallery-slider__swiper');
  
  
  if (!swiperElement) {
    return;
  }

  let swiperThumbs;

  // Wrap inner blocks in swiper-slide divs
  const wrapper = swiperElement.querySelector('.swiper-wrapper');
  if (!wrapper) {
    return;
  }

  // Get all child blocks (images and videos)
  const blocks = Array.from(wrapper.children);
  
  // Clear wrapper and re-add blocks wrapped in swiper-slide
  wrapper.innerHTML = '';
  
  const slideElements = [];
  blocks.forEach((block) => {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    slide.appendChild(block);
    wrapper.appendChild(slide);
    slideElements.push(slide);
  });

  // Base Swiper configuration
  const swiperConfig = {
    direction: direction,
    slidesPerView: slidesPerViewMobile,
    spaceBetween: spaceBetweenMobile,
    centeredSlides: centeredSlidesMobile,
    initialSlide: initialSlide,
    loop: false,
    breakpoints: {
      768: {
        slidesPerView: slidesPerViewDesktop,
        spaceBetween: spaceBetweenDesktop,
        centeredSlides: centeredSlidesDesktop,
      },
    },
  };

  // Configure navigation based on style
  if (navigationStyle === 'arrows') {
    swiperConfig.navigation = {
      nextEl: sliderElement.querySelector('.swiper-button-next'),
      prevEl: sliderElement.querySelector('.swiper-button-prev'),
    };
  } else if (navigationStyle === 'bullets') {
    swiperConfig.pagination = {
      el: sliderElement.querySelector('.swiper-pagination'),
      clickable: true,
      dynamicBullets: true,
    };
  } else if (navigationStyle === 'thumbnails') {
    // Create thumbnails slider first
    const thumbsElement = sliderElement.querySelector('.media-gallery-slider__thumbs');
    
    if (thumbsElement) {
      const thumbsWrapper = thumbsElement.querySelector('.swiper-wrapper');
      
      // Generate thumbnail slides
      slideElements.forEach((slide) => {
        const thumbSlide = document.createElement('div');
        thumbSlide.className = 'swiper-slide';
        
        // Clone the media element for thumbnail
        const mediaElement = slide.querySelector('img, video');
        if (mediaElement) {
          const thumbMedia = mediaElement.cloneNode(true);
          thumbSlide.appendChild(thumbMedia);
          thumbsWrapper.appendChild(thumbSlide);
        }
      });

        swiperThumbs = new Swiper(thumbsElement, {
        spaceBetween: 12,
        slidesPerView: 2,
        centeredSlides: true,
        slideToClickedSlide: true,  
        watchSlidesProgress: true,  
         breakpoints: {
          768: {
            // desktop
            slidesPerView: 5,
            spaceBetween: 24,
          },
        },
      });

 
 
    }
  }

  // Initialize main Swiper instance
  const swiper = new Swiper(swiperElement, swiperConfig);


  if (navigationStyle === 'thumbnails') {
     let isUpdating = false;
    const syncSliders = (sourceSlider, targetSlider, isLoop = false) => {
      return function() {

        // Prevent infinite loop
        if (isUpdating) return;
         
        isUpdating = true;
        
        // Get the correct index
        const sourceIndex = isLoop ? sourceSlider.realIndex : sourceSlider.activeIndex;
        const targetIndex = isLoop ? targetSlider.realIndex : targetSlider.activeIndex;
        
        // Only update if indices are different
        if (sourceIndex !== targetIndex) {
          if (isLoop && targetSlider.params.loop) {
            targetSlider.slideToLoop(sourceIndex);
          } else {
            targetSlider.slideTo(sourceIndex);
          }
        }
        else {
          // If indices are the same, reset flag immediately
          isUpdating = false;
        }
        
      };
    }
    const resetFlag = () => {
      isUpdating = false;
    };
    swiper.on('slideChange', syncSliders(swiper, swiperThumbs, false));
    swiperThumbs.on('slideChange', syncSliders(swiperThumbs, swiper, false));
   
    swiperThumbs.on('slideChangeTransitionEnd', resetFlag);
    swiper.on('slideChangeTransitionEnd', resetFlag);
  }
  
  // Handle bottom arrows navigation with custom buttons
  if (navigationStyle === 'bottom-arrows') {
    const bottomPrev = sliderElement.querySelector('.media-gallery-slider__bottom-prev');
    const bottomNext = sliderElement.querySelector('.media-gallery-slider__bottom-next');

    if (bottomPrev && bottomNext) {
      // Add click handlers
      bottomPrev.addEventListener('click', () => {
        swiper.slidePrev();
      });

      bottomNext.addEventListener('click', () => {
        swiper.slideNext();
      });

      // Update button states on slide change
      const updateButtonStates = () => {
        if (swiper.isBeginning) {
          bottomPrev.disabled = true;
        } else {
          bottomPrev.disabled = false;
        }

        if (swiper.isEnd) {
          bottomNext.disabled = true;
        } else {
          bottomNext.disabled = false;
        }
      };

      // Initialize button states
      updateButtonStates();

      // Update on slide change
      swiper.on('slideChange', updateButtonStates);
    }
  }

  // Add loading animation
  sliderElement.classList.add('is-loading');
  setTimeout(() => {
    sliderElement.classList.remove('is-loading');
  }, 300);

  // Check if slidesPerView is 1 for current viewport
  const isSingleSlideView = () => {
    const isMobile = window.innerWidth < 768;
    return isMobile ? slidesPerViewMobile === 1 : slidesPerViewDesktop === 1;
  };

  // Auto-play video in initial slide if slidesPerView is 1
  if (isSingleSlideView()) {
    const initialActiveSlide = swiper.slides[initialSlide];
    const videoPlayer = getVideoPlayerFromSlide(initialActiveSlide);
    if (videoPlayer) {
      // Small delay to ensure everything is loaded
      setTimeout(() => {
        playSlideVideo(initialActiveSlide);
      }, 500);
    }
  }

  // Video playback control on slide change
  // Pause all videos when slide changes, play active slide video if slidesPerView is 1
  swiper.on('slideChange', () => {
    const activeIndex = swiper.activeIndex;
    
    // Pause all videos except active slide
    pauseAllVideosExcept(swiper, activeIndex);
    
    // Auto-play video in active slide if slidesPerView is 1
    if (isSingleSlideView()) {
      const activeSlide = swiper.slides[activeIndex];
      const videoPlayer = getVideoPlayerFromSlide(activeSlide);
      if (videoPlayer) {
        playSlideVideo(activeSlide);
      }
    }
  });

  // Pause videos when swiper is destroyed or hidden
  swiper.on('destroy', () => {
    swiper.slides.forEach((slide) => {
      pauseSlideVideo(slide);
    });
  });

  return swiper;
}

/**
 * Initialize all sliders on the page
 */
function init() {
  const sliders = document.querySelectorAll('.media-gallery-slider');
  
  sliders.forEach((slider) => {
    // Check if already initialized
    if (slider.dataset.swiperInitialized) {
      return;
    }

    initSlider(slider);
    slider.dataset.swiperInitialized = 'true';
  });
}


// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-initialize on dynamic content load (e.g., AJAX)
document.addEventListener('media-gallery-slider:init', init);

